import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { STRIPE_TEST_MODE } from '@/lib/utils';

interface PaymentVerificationResult {
  isLoading: boolean;
  isVerified: boolean | null;
  paymentData: any;
  error: string | null;
  paymentStatus: 'paid' | 'unpaid' | 'error' | null;
}

const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 3000]; // Increasing delays between retries

export const usePaymentVerification = (sessionId: string | null): PaymentVerificationResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'unpaid' | 'error' | null>(null);

  // Use refs to prevent duplicate processing across React StrictMode and component re-renders
  const processedSessionRef = useRef<string | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Prevent processing the same sessionId more than once OR if already processing
    if (!sessionId || sessionId === processedSessionRef.current || isProcessingRef.current) {
      return;
    }

    // Mark as processing immediately to prevent race conditions
    isProcessingRef.current = true;
    processedSessionRef.current = sessionId;

    const verifyPaymentWithRetries = async () => {
      setIsLoading(true);
      setError(null);

      let lastError: Error | null = null;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          console.log(`[PaymentVerification] Attempt ${attempt + 1}/${MAX_RETRIES} for session:`, sessionId);

          const { data, error: verifyError } = await supabase.functions.invoke('verify-payment-v3', {
            body: { sessionId, testMode: STRIPE_TEST_MODE }
          });

          if (verifyError) {
            throw verifyError;
          }

          console.log('[PaymentVerification] Result:', data);

          if (data.paymentStatus === 'paid') {
            setIsVerified(true);
            setPaymentData(data);
            setPaymentStatus('paid');

            // Save event to localStorage if we have the event data in metadata
            if (data.metadata?.event_id) {
              const eventData = {
                id: data.metadata.event_id,
                title: data.metadata.event_title,
                amount: data.amountTotal / 100,
                currency: data.currency,
                payment_date: new Date().toISOString(),
                payment_status: 'paid'
              };

              const existingEvents = JSON.parse(localStorage.getItem('myUpcomingEvents') || '[]');
              const updatedEvents = [...existingEvents, eventData];
              localStorage.setItem('myUpcomingEvents', JSON.stringify(updatedEvents));
            }

            // Success is shown in the UI, no toast needed
            setIsLoading(false);
            isProcessingRef.current = false;
            return; // Success - exit the retry loop
          } else {
            // Payment not paid - this is a final state from Stripe, don't retry
            setIsVerified(false);
            setPaymentStatus('unpaid');
            setError('El pago no pudo ser verificado');
            // Error is shown in the UI, no toast needed
            setIsLoading(false);
            isProcessingRef.current = false;
            return;
          }
        } catch (err) {
          lastError = err instanceof Error ? err : new Error('Error desconocido');
          console.error(`[PaymentVerification] Attempt ${attempt + 1} failed:`, err);

          // If not the last attempt, wait before retrying
          if (attempt < MAX_RETRIES - 1) {
            console.log(`[PaymentVerification] Retrying in ${RETRY_DELAYS[attempt]}ms...`);
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          }
        }
      }

      // All retries exhausted - network/connection error, NOT a Stripe rejection
      console.error('[PaymentVerification] All retries exhausted:', lastError);
      setIsVerified(false);
      setPaymentStatus('error');
      setError(lastError?.message || 'Error desconocido');
      // Error is shown in the UI, no toast needed
      setIsLoading(false);
      isProcessingRef.current = false;
    };

    verifyPaymentWithRetries();
  }, [sessionId]);

  return { isLoading, isVerified, paymentData, error, paymentStatus };
};
